import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { logError } from '../lib/errorLogger';

function isChunkError(error) {
  if (!error) return false;
  const msg = error.message || error.toString();
  return (
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes("Cannot find module") ||
    msg.includes("Unexpected token '<'")
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('Error Boundary caught:', error, errorInfo);

    // iter 375k — Report render crashes to /api/error-logs. Boundary-caught
    // errors never hit window.onerror in production, so Jake's "Something
    // went wrong" screens were invisible in Settings → Diagnostics.
    if (!isChunkError(error)) {
      logError({
        action: 'react_render_crash',
        error,
        source: 'error_boundary',
        extra: { component_stack: errorInfo?.componentStack?.slice(0, 2000) || null },
      });
    }

    // Auto-reload once for chunk load errors (stale deploy)
    if (isChunkError(error)) {
      const lastReload = sessionStorage.getItem('chunk_reload');
      const now = Date.now();
      // Only auto-reload if we haven't reloaded in the last 30 seconds
      if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
        sessionStorage.setItem('chunk_reload', now.toString());
        window.location.reload();
        return;
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const isChunk = isChunkError(this.state.error);
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3B30]/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-[#FF3B30]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              {isChunk ? "New update available" : "Something went wrong"}
            </h2>
            <p className="text-zinc-300 text-sm mb-4">
              {isChunk
                ? "A new version was deployed. Refreshing will fix this."
                : (this.props.fallbackMessage || "An error occurred while rendering this section.")}
            </p>
            <div className="flex gap-2 justify-center">
              {isChunk ? (
                <Button
                  onClick={() => window.location.reload()}
                  className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Now
                </Button>
              ) : (
                <>
                  <Button
                    onClick={this.handleReset}
                    className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Reload Page
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
